/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { TunePadCell } from './cell';
import { TunePadProject } from './project';
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { yCollab } from 'y-codemirror.next'
import { TunePadSample } from './sample';

/**
 * Persistence provider using Y.js web socket provider
 */
export class ProjectPersistence {

    private provider : WebsocketProvider;
    private yProject : Y.Map<string>;
    private yCells : Y.Map<Y.Text>;
    private project : TunePadProject;

    private static instance : ProjectPersistence | undefined;


    /**
     * Using a singleton pattern to avoid creating multiple yDocs.
     * This gets called from TunePadProject.
     */
    public static Initialize(project : TunePadProject) {
        ProjectPersistence.instance = ProjectPersistence.instance ?? new ProjectPersistence(project);
    }


    /**
     * Set up the yDoc and websocket provider. Then wait for sync...
     */
    private constructor(project : TunePadProject) {
        this.project = project;

        // create main Y.Doc and project Y.Map
        const yDoc = new Y.Doc();
        this.yProject = yDoc.getMap('project');
        this.yCells = yDoc.getMap('cells');


        // connect to the persistence provider
        const wss = project.getAttribute('persistence') as string;
        this.provider = new WebsocketProvider(wss, project.uuid, yDoc)
        //wsProvider.on('status', event => { console.log(event.status) });


        // set awareness
        this.setAwareness();


        // wait for the provider to sync
        this.provider.once('sync', async ok => { if (ok) this.init() });
    }


    /**
     * This gets called after the provider has synced up with the datastore.
     */
    private async init() {

        // set initial project data from yjs
        this.yLoad();

        // listen for local project changes
        this.project.addEventListener('update-project', e => this.ySave());
        this.project.addEventListener('add-cell', e => this.addCell((e as CustomEvent).detail.instrument));
        this.project.addEventListener('copy-cell', e => this.copyCell((e as CustomEvent).detail.uuid));
        this.project.addEventListener('reorder-cells', e => {
            const detail = (e as CustomEvent).detail;
            const cell = this.project.reorderCells(detail.moved, detail.newPosition);
            if (cell) this.bindYText(cell);
            this.ySave();
        });

        // bind yText to codemirror for each cell
        document.querySelectorAll('tunepad-cell').forEach(cell => this.bindYText(cell as TunePadCell));



        // listen for remote changes to project data
        this.yProject.observe(evt => {
            evt.changes.keys.forEach((change, key) => {
                if (change.action === 'add' || change.action === 'update') {
                    this.yLoad();
                } else if (change.action === 'delete') {
                    console.log('delete project??')
                }
            });

            // update last saved info
            document.querySelectorAll('.last-saved').forEach(el => { 
                el.innerHTML = `Last Saved: ${this.formatDate()}`
            });
        });


        // initial compile of all of the cells
        await this.waitForElementConnected(this.project);
        await this.project.recompileAll();

        this.project.dispatchEvent(new CustomEvent('persistence-synced', { 
            bubbles: false, composed: true, detail : { uuid : this.project.uuid }
        }));
    }


    private ySave() {
        this.yProject.set('data', JSON.stringify(this.project.save()));
    }


    private yLoad() {
        try {
            if (this.yProject.has('data')) {
                const data = JSON.parse(this.yProject.get('data') as string);

                // restore project from json data
                const cell_list = this.project.restore(data, false);
                const cell_ids = Object.keys(cell_list);

                // delete cells that are no longer in yjs
                document.querySelectorAll('tunepad-cell, tunepad-sample').forEach(cell => {
                    if (!cell_ids.includes(cell.getAttribute('uuid') ?? '')) cell.remove();
                });

                // compile and bind ytext objects for newly added cells
                this.project.querySelectorAll('tunepad-cell').forEach(cell => {
                    const uuid = (cell as TunePadCell).uuid;
                    if (!cell_list[uuid]) {
                        this.waitForElementConnected(cell).then(ready => {
                            (cell as TunePadCell).recompile(true);
                            this.bindYText(cell as TunePadCell);
                        });
                    }
                });

                // do we need a reorder?
                while (this.reorder(cell_ids));
            }
        } catch (e) {
            console.log('Error in yLoad', e);
        }
    }


    /**
     * From <tunepad-project awareness="username:usercolor">
     */
    private setAwareness() {
        let username = this.project.getAttribute('awareness') || 'Anonymous';
        let usercolor = 'rgb(160, 120, 200)';

        if (username.includes(':')) {
            usercolor = username.split(':')[1];
            username = username.split(':')[0];
        }

        this.provider.awareness.setLocalStateField('user', {
            name: username,
            color: usercolor,
            colorLight: usercolor
        });
    }


    /**
     * When the add cell button is pressed
     */
    private async addCell(instrument : string = 'piano') {
        if (instrument === 'sound') {
            const sample = document.createElement('tunepad-sample') as TunePadSample;
            sample.setAttribute('uuid', crypto.randomUUID());
            document.querySelector('tunepad-cell-list')?.append(sample);
            sample.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.ySave();
            return;
        }

        const attribs : any = { };
        attribs.theme = 'bitcrush';
        attribs.autocompile = 'true';
        attribs.class = 'tutorial music';
        attribs.timeline = 'midi';
        attribs.name = 'new ' + instrument;
        if (instrument === 'bass') {
            attribs.patch = 'electric-bass';
        }
        else if (instrument === 'drums') {
            attribs.patch = '808-drums';
        }
        else if (instrument === 'guitar') {
            attribs.patch = 'electric-guitar';
        }
        else if (instrument === 'canvas') {
            attribs.class = 'tutorial animation';
            attribs.timeline = 'canvas';
            attribs.name = 'canvas';
        }
        else {
            //if (instrument === 'piano' || instrument === 'keys') {
            attribs.patch = 'grand-piano';
        }

        // create the new <tunepad-cell> and set attributes
        const cell = document.createElement('tunepad-cell') as TunePadCell;
        Object.entries(attribs).forEach(([key, value]) => {
            cell.setAttribute(key, value as string);
        });
        cell.setAttribute('uuid', cell.uuid);

        // add the cell to the cell list
        document.querySelector('tunepad-cell-list')?.append(cell);

        // after bind events and ytext
        const initialText = (instrument === 'canvas') ? DEFAULT_CANVAS_CODE : DEFAULT_CODE;
        cell.replaceCode(initialText);
        this.bindYText(cell, initialText);

        // recompile after loading
        await this.waitForElementConnected(cell);
        cell.scrollIntoView({ behavior: 'smooth', block: 'center' });

        cell.recompile(true);
        this.ySave();
    }


    private async copyCell(uuid : string) : Promise<TunePadCell> {
        // create the new <tunepad-cell> and copy the attributes over
        const original = document.querySelector(`tunepad-cell[uuid="${uuid}"]`) as TunePadCell;
        const cell = document.createElement('tunepad-cell') as TunePadCell;
        original.getAttributeNames().forEach(attrib => {
            if (attrib !== 'uuid') {
                cell.setAttribute(attrib, `${original.getAttribute(attrib)}`);
            }
        });
        cell.setAttribute('uuid', cell.uuid);

        // add the cell to the cell list
        document.querySelector('tunepad-cell-list')?.append(cell);

        // after bind events and ytext
        const code = original.code;
        cell.replaceCode(code);
        this.bindYText(cell, code);

        // recompile after loading
        await this.waitForElementConnected(cell);
        cell.scrollIntoView({ behavior: 'smooth', block: 'center' });

        cell.recompile(true);
        this.ySave();
        return cell;
    }


    private bindYText(cell : TunePadCell, initialText : string | undefined = undefined) {
        const uuid = cell.getAttribute('uuid') as string;
        const editor = cell.getEditor();
        const existing = this.yCells.has(uuid);
        const code = (editor as any).code;
        const ytext = existing ? this.yCells.get(uuid)! : new Y.Text();
        if (existing) {
            cell.replaceCode(ytext.toString());
        } else {
            ytext.insert(0, initialText ?? code);
            this.yCells.set(uuid, ytext);
        }
        const undoManager = new Y.UndoManager(ytext);
        const extensions = yCollab(ytext, this.provider.awareness, { undoManager });
        this.waitForElementConnected(editor).then(ready => {
            editor.bindCollaboration([ extensions ]);
        });
    }


    private formatDate(date : number = -1) {
        if (date < 0) date = Date.now();
        if (typeof date === 'string') date = Date.parse(date);
        return this.date_formatter.format(date);
    }
    private date_formatter = new Intl.DateTimeFormat('en-US', {
        month: 'long', day: 'numeric',  hour: '2-digit', minute: '2-digit'
    });


    // compare the current <tunepad-cell> element ordering to the data object ordering
    private reorder(ordering : string[]) : boolean {
        const cells = document.querySelectorAll('tunepad-cell, tunepad-sample');

        // this should never happen, but just in case
        if (cells.length !== ordering.length) return false;

        for (let i=0; i < (cells.length - 1); i++) {
            const uuid = `${cells[i].getAttribute('uuid')}`;

            // we have a mismatch? which means either this cell moved down or another cell moved up
            let newCell = undefined;
            if (uuid !== ordering[i]) {
                if (uuid === ordering[i+1]) {
                    newCell = this.project.reorderCells(ordering[i], i); // another moved up
                } else {
                    newCell = this.project.reorderCells(uuid, ordering.indexOf(uuid)); // this moved down
                }
                if (newCell) {
                    this.bindYText(newCell);
                }
                return true; // indicate that there was at least one reordering
            }
        }
        return false;
    }


    private waitForElementConnected(element : Element) : Promise<boolean> {
        return new Promise(resolve => {
            function checkConnection() {
                if (element.isConnected) {
                    resolve(true);
                } else {
                    requestAnimationFrame(checkConnection);
                }
            }
            requestAnimationFrame(checkConnection);
        });
    }
}

const DEFAULT_CANVAS_CODE = `canvas = getCanvas()
canvas.clearRect(0, 0, canvas.width, canvas.height)

# draw some purple text
canvas.fillStyle = 'purple'
canvas.font = '30px sans-serif'  # sets the font size and style
canvas.fillText('안녕, Hola, 你好, Hello, привіт', 50, 50)


# draw some emojis
canvas.font = '50px sans-serif'
canvas.fillText('🔥🎵🎸🎷', 50, 130)


# draw a cyan circle
canvas.fillStyle = 'cyan'
canvas.fillCircle(300, 200, 50)     # (x, y, radius)


# draw a pink rectangle
canvas.fillStyle = 'pink'
canvas.fillRect(400, 200, 150, 45)  # (x, y, width, height)




`;

const DEFAULT_CODE = '# add code here...\n\n\n\n\n\n\n\n\n\n';